// src/pages/unit/RectoreUnit.js
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const galleryImages = [
  { src: "/images/recg1.jpg", caption: "Mall. Alfa Mohammed (Secretary, Rector's Office) " },
  { src: "/images/recg2.jpg", caption: "Staff" },
  { src: "/images/itsg.jpg", caption: " " },
  { src: "/images/tsg4.jpg", caption: " " },
  { src: "/images/tsg5.jpg", caption: " " },
  { src: "/images/tsg6.jpg", caption: " " },
  { src: "/images/tsg7.jpg", caption: "" },
  { src: "/images/tsg8.jpg", caption: " " },
];

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.08 },
  }),
};

const RectorUnit = () => {
  const [currentIndex, setCurrentIndex] = useState(null);

  const openModal = (index) => setCurrentIndex(index);
  const closeModal = () => setCurrentIndex(null);

  const showPrev = () =>
    setCurrentIndex((prev) =>
      prev > 0 ? prev - 1 : galleryImages.length - 1
    );
  const showNext = () =>
    setCurrentIndex((prev) =>
      prev < galleryImages.length - 1 ? prev + 1 : 0
    );

  // Keyboard navigation (ESC, ←, →)
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") closeModal();
      if (e.key === "ArrowLeft") showPrev();
      if (e.key === "ArrowRight") showNext();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Banner */}
      <section className="relative h-[300px] md:h-[400px] flex items-center justify-center">
        <img
          src="/images/slides.jpg"
          alt="Department Banner"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative text-center text-white px-6">
          <motion.h1
            className="text-3xl md:text-5xl font-extrabold mb-3"
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            Rectory Unit
          </motion.h1>
          <motion.p
            className="text-lg md:text-xl max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            Provides overall leadership and guidance to the entire institution
          </motion.p>
        </div>
      </section>


        {/* Services Section */}
        <motion.section
          className="bg-white shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">Services</h2>
          <ul className="grid md:grid-cols-2 gap-1 text-gray-600 list-disc pl-25 text-lg">
            <li>Coordination of Principal Officers</li>
            <li>Strategic Leadership & Policy Directiont</li>
            <li>Student Welfare, Discipline & Engagement</li>
            <li>External Relations & Partnerships</li>
          </ul>
        </motion.section>
         {/* Activities Section */}
        <motion.section
          className="bg-white shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">Activities</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                title: "Hosting Official Events",
                img: "/images/reca1.webp",
              },
              {
                title: "Monitoring & Evaluation of Institutional Performance",
                img: "/images/reca3.jpeg",
              },
              {
                title: "Crisis Management & Conflict Resolution",
                img: "/images/reca5.jpg",
              },
              {
                title: "Issues circulars, directives, and memos",
                img: "/images/reca6.jpg",
              },
            ].map((activity, i) => (
              <div
                key={i}
                className="bg-gray-50 rounded-lg shadow hover:shadow-lg transition p-4"
              >
                <img
                  src={activity.img}
                  alt={activity.title}
                  className="w-full h-40 object-cover rounded-md mb-4"
                />
                <h3 className="font-semibold text-gray-800">{activity.title}</h3>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Gallery Section with Lightbox */}
        <motion.section
          className="mb-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
            Gallery
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6 px-4 sm:px-0">
            {galleryImages.map((img, index) => (
              <motion.figure
                key={index}
                custom={index}
                variants={cardVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                whileHover={{ y: -4, scale: 1.03 }}
                className="relative rounded-xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500 group cursor-pointer"
                onClick={() => openModal(index)}
              >
                <img
                  src={img.src}
                  alt={img.caption}
                  loading="lazy"
                  className="w-full aspect-square object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <figcaption className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition duration-500 flex items-center justify-center">
                  <p className="text-white text-sm sm:text-lg font-semibold px-2 text-center">
                    {img.caption}
                  </p>
                </figcaption>
              </motion.figure>
            ))}
          </div>

          {/* Lightbox Modal */}
          <AnimatePresence>
            {currentIndex !== null && (
              <motion.div
                className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={closeModal}
              >
                <div
                  className="relative w-full max-w-4xl px-4"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={closeModal}
                    className="absolute top-4 right-4 text-white hover:text-gray-300"
                  >
                    <X className="w-8 h-8" />
                  </button>

                  <button
                    onClick={showPrev}
                    className="absolute top-1/2 -left-6 transform -translate-y-1/2 text-white hover:text-gray-300"
                  >
                    <ChevronLeft className="w-10 h-10" />
                  </button>

                  <button
                    onClick={showNext}
                    className="absolute top-1/2 -right-6 transform -translate-y-1/2 text-white hover:text-gray-300"
                  >
                    <ChevronRight className="w-10 h-10" />
                  </button>

                  <motion.img
                    key={galleryImages[currentIndex].src}
                    src={galleryImages[currentIndex].src}
                    alt={galleryImages[currentIndex].caption}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="w-full rounded-xl object-contain max-h-[80vh] mx-auto"
                  />
                  <p className="text-center text-white mt-4 text-base sm:text-lg">
                    {galleryImages[currentIndex].caption}
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.section>

        {/* Contact Section */}
        <motion.section
          className="bg-white shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Contact the Directorate
          </h2>
          <p className="text-gray-600 mb-2">
            <strong>Address:</strong> Federal Polytechnic, Bida, Niger State,
            Nigeria
          </p>
          <p className="text-gray-600 mb-2">
            <strong>Email:</strong> rectory.unit@fedpolybida.edu.ng
          </p>
          <p className="text-gray-600 mb-2">
            <strong>Phone:</strong> +234 903 577 9114
          </p>
        </motion.section>

        {/* Map Section */}
        <motion.section
          className="rounded-2xl overflow-hidden shadow-md"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7 }}
        >
          <iframe
            title="Unit Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3931.203503579147!2d6.0079231!3d9.0391016!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x104966b7f2e9b195%3A0x18c8eec7a2fae37c!2sFederal+Polytechnic+Bida%2C+Doko+Road%2C+Bida%2C+Niger+State!5e0!3m2!1sen!2sus!4v1694784000000!5m2!1sen!2sus"
            width="100%"
            height="400"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          ></iframe>
        </motion.section>
      </div>
  );
};

export default RectorUnit;
